import {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
  type FC,
} from "react";
import { Box, IconButton } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import type { ColDef, ICellRendererParams } from "ag-grid-community";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  apiGet,
  apiPost,
  apiPut,
  apiDelete,
} from "../../services/genesisApi/httpsMethods";
import type { ApiResponse } from "../../services/genesisApi/httpsMethods";
import { CustomButton } from "../../components/common/customButton";
import { ErrorDialog } from "../../components/common/errorDialog";
import { useErrorDialog } from "../../hooks/useErrorDialog";
import { useReferenceLists } from "../../context/GlobalGetLists";
import { useCurrentUser } from "../../context/CurrentUserContext";
import {
  createSingleSelectColumn,
  createStringSelectColumn,
} from "../../utils/columnHelpers";
import {
  currencyFormatter,
  percentageFormatter,
  dateFormatter,
  normaliseToDateInput,
} from "../../utils/numberFormatter";
import { MUISelectCellEditor } from "../../components/common/gridSelectCell";
import type { Accordion, Status } from "../../types/deal";
import "../../assets/css/AGGrid-custom.css";
import { useGridEditCommit } from "@/hooks/useGridEditCommit";

ModuleRegistry.registerModules([AllCommunityModule]);

interface DealAccordionsTableProps {
  dealId: number | undefined;
  accordionsRef: React.RefObject<HTMLDivElement | null>;
  accordionsOrginal: Accordion[]; // Pass original accordions for change tracking
}

type AccordionRow = Omit<Accordion, "status_id" | "id"> & {
  id: number | string;
  status_id: number | null;
  status?: Status | null;
};

const DealAccordionsTable: FC<DealAccordionsTableProps> = ({
  dealId,
  accordionsRef,
  accordionsOrginal,
}) => {
  const [accordions, setAccordions] =
    useState<AccordionRow[]>(accordionsOrginal);
  const [originalAccordions, setOriginalAccordions] =
    useState<AccordionRow[]>(accordionsOrginal);
  const [editMode, setEditMode] = useState(false);
  const [dirtyGrid, setDirtyGrid] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { errorDialog, showErrorFromResponse, hideError } = useErrorDialog();
  const accordionsGridRef = useRef<AgGridReact<AccordionRow>>(null);
  const { canEdit } = useCurrentUser();
  const { status, currencies } = useReferenceLists();

  // Parent fetches async; tab panels stay mounted, so sync when props arrive.
  useEffect(() => {
    setAccordions(accordionsOrginal as AccordionRow[]);
    setOriginalAccordions(
      structuredClone(accordionsOrginal) as AccordionRow[],
    );
    setEditMode(false);
  }, [accordionsOrginal]);

  const onGridReady = useCallback(() => {
    if (accordionsGridRef.current?.api) {
      accordionsGridRef.current.api.applyColumnState({
        state: [{ colId: "target_closing", sort: "asc" }],
        defaultState: { sort: null },
      });
    }
  }, []);

  // Apply sort when accordions data changes
  useEffect(() => {
    if (accordionsGridRef.current?.api && accordions.length > 0) {
      accordionsGridRef.current.api.applyColumnState({
        state: [{ colId: "target_closing", sort: "asc" }],
        defaultState: { sort: null },
      });
    }
  }, [accordions]);

  // Track if grid is dirty
  useEffect(() => {
    const isEqual =
      JSON.stringify(accordions) === JSON.stringify(originalAccordions);
    setDirtyGrid(!isEqual);
  }, [accordions, originalAccordions]);

  // Transform currencies string array to Currency objects for the dropdown
  const currencyObjects = useMemo((): { id: string; currency: string }[] => {
    return (currencies || []).map((currency: string) => ({
      id: currency,
      currency: currency,
    }));
  }, [currencies]);

  // Accordion type options with display labels
  const accordionTypeOptions = [
    { id: "pipeline", label: "Pipeline" },
    { id: "funded", label: "Funded" },
    { id: "baseline_retrospective", label: "Baseline" },
  ];

  // Funding type options (only for baseline accordions)
  const fundingTypeOptions = ["add-on", "up-size"];

  // Delete icon cell renderer (first column)
  const DeleteIconRenderer = (params: ICellRendererParams<AccordionRow>) => {
    if (!editMode) return null;
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
        }}
      >
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            if (params.data?.id) {
              handleDeleteRow(params.data.id);
            }
          }}
          sx={{ padding: "2px", color: "#d32f2f" }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  };

  const handleDeleteRow = async (accordionId: number | string) => {
    if (!confirm("Are you sure you want to delete this addon?")) {
      return;
    }

    // Remove from local state immediately
    setAccordions((prev) => prev.filter((a) => a.id !== accordionId));

    // If it's an existing accordion (numeric id), delete from backend
    if (typeof accordionId === "number" && dealId) {
      try {
        const response = await apiDelete("accordions", accordionId);
        if (!response || !response.success) {
          console.error("Failed to delete accordion:", response?.message);
          // Reload accordions on error
          if (dealId) {
            const reloadResponse = await apiGet(`deals/${dealId}/accordions`);
            if (
              reloadResponse &&
              reloadResponse.success &&
              reloadResponse.data
            ) {
              const fetched = Array.isArray(reloadResponse.data)
                ? reloadResponse.data
                : [];
              setAccordions(fetched as AccordionRow[]);
              setOriginalAccordions(structuredClone(fetched as AccordionRow[]));
            }
          }
        } else {
          // Update original accordions
          setOriginalAccordions((prev) =>
            prev.filter((a) => a.id !== accordionId),
          );
        }
      } catch (error) {
        console.error("Error deleting accordion:", error);
        // Reload accordions on error
        if (dealId) {
          const reloadResponse = await apiGet(`deals/${dealId}/accordions`);
          if (reloadResponse && reloadResponse.success && reloadResponse.data) {
            const fetched = Array.isArray(reloadResponse.data)
              ? reloadResponse.data
              : [];
            setAccordions(fetched as AccordionRow[]);
            setOriginalAccordions(structuredClone(fetched as AccordionRow[]));
          }
        }
      }
    }
  };

  const handleAddRow = () => {
    if (!dealId) return;
    const newRow: AccordionRow = {
      id: `new-accordion-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      deal_id: dealId,
      accordion_name: "",
      funding_type: "add-on",
      loan_amount_day_one: null,
      loan_amount_fully_funded: null,
      loan_amount_currency: null,
      margin: null,
      ltc: null,
      ltv: null,
      irr_local: null,
      irr_usd: null,
      irr_local_net: null,
      irr_usd_net: null,
      wal: null,
      box_folder_id: null,
      status_id: null,
      accordion_type: "pipeline",
      funding_sequence_number: null,
      parent_accordion_id: null,
      loan_id: null,
      proposed_ic_date: null,
      target_signing: null,
      target_closing: null,
      funded_date: null,
      created_date: new Date().toISOString(),
      updated_date: null,
    };
    setAccordions((prev) => [...prev, newRow]);
  };

  const onCellValueChanged = useCallback((event: any) => {
    const { colDef, newValue, data, api } = event;
    const field = colDef.field;

    // Handle loan_amount_currency - extract string value from object if needed
    let valueToStore = newValue;
    if (field === "loan_amount_currency") {
      if (
        typeof newValue === "object" &&
        newValue !== null &&
        newValue.currency
      ) {
        valueToStore = newValue.currency;
      } else if (typeof newValue === "string") {
        valueToStore = newValue;
      }
    }

    // Handle funding_type - extract string value from object if needed
    if (field === "funding_type") {
      if (typeof newValue === "object" && newValue !== null && newValue.id) {
        valueToStore = newValue.id;
      } else if (typeof newValue === "string") {
        valueToStore = newValue;
      }
    }

    // Update state
    setAccordions((prev) => {
      const updated = prev.map((a) =>
        a.id === data.id ? { ...a, [field]: valueToStore } : a,
      );
      return updated;
    });

    // Force refresh the cell to update display
    if (field === "loan_amount_currency" || field === "funding_type") {
      setTimeout(() => {
        api.refreshCells({ rowNodes: [event.node], columns: [field] });
      }, 0);
    }
  }, []);

  const handleCancelButtonPress = () => {
    setEditMode(false);
    setAccordions(structuredClone(originalAccordions));
    setDirtyGrid(false);
  };

  const handleSaveButtonPress = async () => {
    if (!dealId || isSaving) return;

    setIsSaving(true);
    try {
      // Get changed items
      const changedItems = accordions.filter((accordion, index) => {
        const initial = originalAccordions[index];
        if (!initial) return true; // New items
        return JSON.stringify(accordion) !== JSON.stringify(initial);
      });

      // Helper to extract status_id from status object
      const getStatusId = (accordion: AccordionRow): number | null => {
        if (accordion.status_id && typeof accordion.status_id === "number")
          return accordion.status_id;
        if (
          accordion.status &&
          typeof accordion.status === "object" &&
          accordion.status.id
        )
          return accordion.status.id;
        return null;
      };

      // Helper to extract funding_type string value
      const getFundingType = (accordion: AccordionRow): string | null => {
        const fundingType = accordion.funding_type;
        if (!fundingType) return null;
        if (typeof fundingType === "string") return fundingType;
        if (
          typeof fundingType === "object" &&
          fundingType !== null &&
          "id" in fundingType
        ) {
          return (fundingType as { id: string }).id;
        }
        return null;
      };

      // Update existing accordions
      const updatePromises = changedItems
        .filter((accordion) => typeof accordion.id === "number")
        .map((accordion) => {
          const statusId = getStatusId(accordion);
          const fundingType = getFundingType(accordion);
          const payload: any = {
            accordion_name: accordion.accordion_name,
            loan_amount_day_one: accordion.loan_amount_day_one,
            loan_amount_fully_funded: accordion.loan_amount_fully_funded,
            loan_amount_currency: accordion.loan_amount_currency,
            margin: accordion.margin,
            ltc: accordion.ltc ?? null,
            ltv: accordion.ltv ?? null,
            irr_local: accordion.irr_local ?? null,
            irr_usd: accordion.irr_usd ?? null,
            irr_local_net: accordion.irr_local_net ?? null,
            irr_usd_net: accordion.irr_usd_net ?? null,
            wal: accordion.wal ?? null,
            box_folder_id: accordion.box_folder_id ?? null,
            status_id: statusId,
            accordion_type: accordion.accordion_type,
            funding_type: fundingType,
            proposed_ic_date: accordion.proposed_ic_date,
            target_signing: accordion.target_signing,
            target_closing: accordion.target_closing,
            funded_date: accordion.funded_date,
          };
          return apiPut("accordions", accordion.id as number, payload).catch(
            (): ApiResponse => ({
              success: false,
              status: 0,
              message: "Failed to save addon. Please try again.",
            }),
          );
        });

      // Create new accordions
      const newAccordions = changedItems.filter((accordion) => {
        const id = accordion.id;
        return typeof id === "string" && id.startsWith("new-accordion-");
      });
      const createPromises = newAccordions.map((accordion) => {
        const statusId = getStatusId(accordion);
        const fundingType = getFundingType(accordion);
        const payload: any = {
          accordion_name: accordion.accordion_name,
          loan_amount_day_one: accordion.loan_amount_day_one,
          loan_amount_fully_funded: accordion.loan_amount_fully_funded,
          loan_amount_currency: accordion.loan_amount_currency,
          margin: accordion.margin,
          ltc: accordion.ltc ?? null,
          ltv: accordion.ltv ?? null,
          irr_local: accordion.irr_local ?? null,
          irr_usd: accordion.irr_usd ?? null,
          irr_local_net: accordion.irr_local_net ?? null,
          irr_usd_net: accordion.irr_usd_net ?? null,
          wal: accordion.wal ?? null,
          box_folder_id: accordion.box_folder_id ?? null,
          status_id: statusId,
          accordion_type: accordion.accordion_type,
          funding_type: fundingType,
          proposed_ic_date: accordion.proposed_ic_date,
          target_signing: accordion.target_signing,
          target_closing: accordion.target_closing,
          funded_date: accordion.funded_date,
        };
        return apiPost(`deals/${dealId}/accordions`, payload).catch(
          (): ApiResponse => ({
            success: false,
            status: 0,
            message: "Failed to create addon. Please try again.",
          }),
        );
      });

      const [updateResults, createResults] = await Promise.all([
        Promise.all(updatePromises),
        Promise.all(createPromises),
      ]);
      const failedResponse = [...updateResults, ...createResults].find(
        (response) => !response?.success,
      );

      if (failedResponse) {
        // Swap temp ids for real ids on addons that were created, so a retry
        // updates them instead of creating duplicates.
        const createdIds = new Map<string, number>();
        createResults.forEach((response, index) => {
          const createdId = (response?.data as { id?: unknown } | undefined)
            ?.id;
          if (response?.success && typeof createdId === "number") {
            createdIds.set(newAccordions[index].id as string, createdId);
          }
        });
        if (createdIds.size > 0) {
          setAccordions((prev) =>
            prev.map((accordion) =>
              typeof accordion.id === "string" && createdIds.has(accordion.id)
                ? { ...accordion, id: createdIds.get(accordion.id) as number }
                : accordion,
            ),
          );
        }
        // Stay in edit mode and keep the user's edits so they can retry.
        showErrorFromResponse(failedResponse);
        setIsSaving(false);
        return;
      }

      // Refresh the page after successful save
      window.location.reload();
    } catch (error) {
      console.error("Error saving accordions:", error);
      showErrorFromResponse({
        success: false,
        status: 0,
        message: "Failed to save addons. Please try again.",
      });
      setIsSaving(false);
    }
  };

  const { isCellEditing, requestSave, gridEditProps } =
    useGridEditCommit(handleSaveButtonPress);

  const percentageCol = (
    field: keyof AccordionRow,
    headerName: string,
    width = 120,
  ): ColDef<AccordionRow> => ({
    field,
    headerName,
    width,
    editable: editMode,
    valueFormatter: percentageFormatter,
    valueParser: (params) => {
      const value = params.newValue;
      if (value === null || value === undefined || value === "") return null;
      const num = parseFloat(value);
      return isNaN(num) ? null : num;
    },
  });

  // Column definitions for accordions table
  const accordionsColumnDefs = useMemo<ColDef<AccordionRow>[]>(
    () => [
      {
        headerName: "",
        width: 50,
        cellRenderer: DeleteIconRenderer,
        sortable: false,
        filter: false,
        editable: false,
        pinned: "left",
      },
      {
        field: "accordion_name",
        headerName: "Addon Name",
        width: 200,
        editable: (params) => {
          // Name is not editable for baseline addons (it's the initial deal name)
          return (
            editMode && params.data?.accordion_type !== "baseline_retrospective"
          );
        },
        pinned: "left",
      },
      {
        ...(createSingleSelectColumn(
          "status",
          "Status",
          status || [],
          "status",
          150,
          editMode,
        ) as ColDef<AccordionRow>),
        valueGetter: (params) => {
          const statusId = params.data?.status_id;
          if (!statusId) return null;
          return status?.find((s: Status) => s.id === statusId) || null;
        },
        valueSetter: (params) => {
          const statusObj = params.newValue;
          if (statusObj && typeof statusObj === "object" && statusObj.id) {
            params.data.status_id = statusObj.id;
            params.data.status = statusObj as any;
            return true;
          }
          return false;
        },
      } as ColDef<AccordionRow>,
      {
        field: "loan_amount_day_one",
        headerName: "Day One Amount",
        width: 150,
        editable: editMode,
        valueFormatter: currencyFormatter,
        valueParser: (params) => {
          const value = params.newValue;
          if (value === null || value === undefined || value === "")
            return null;
          const num = parseFloat(value);
          return isNaN(num) ? null : num;
        },
      },
      {
        field: "loan_amount_fully_funded",
        headerName: "Fully Funded Amount",
        width: 150,
        editable: editMode,
        valueFormatter: currencyFormatter,
        valueParser: (params) => {
          const value = params.newValue;
          if (value === null || value === undefined || value === "")
            return null;
          const num = parseFloat(value);
          return isNaN(num) ? null : num;
        },
      },
      {
        field: "loan_amount_currency",
        headerName: "Currency",
        editable: editMode,
        width: 100,
        cellEditor: MUISelectCellEditor,
        cellEditorParams: {
          values: currencyObjects.map((opt) => ({
            id: opt.id,
            label: opt.currency,
          })),
          originalValues: currencyObjects,
          label: "Currency",
          labelKey: "currency",
          multiple: false,
        },
        valueGetter: (params) => {
          const currencyString = params.data?.loan_amount_currency;
          if (!currencyString) return null;
          return (
            currencyObjects.find((c) => c.currency === currencyString) || null
          );
        },
        valueFormatter: (params) => {
          if (!params.value) return "";
          // Handle both string and object formats
          const currencyValue =
            typeof params.value === "string"
              ? params.value
              : params.value?.currency || "";
          const option = currencyObjects.find(
            (opt) => opt.currency === currencyValue,
          );
          return option?.currency || "";
        },
        valueSetter: (params) => {
          // Store the string currency value
          const stringValue =
            typeof params.newValue === "string"
              ? params.newValue
              : params.newValue?.currency || "";
          if (params.data) {
            params.data.loan_amount_currency = stringValue || null;
            return true;
          }
          return false;
        },
      },
      percentageCol("margin", "Margin (%)"),
      percentageCol("ltc", "LTC (%)", 100),
      percentageCol("ltv", "LTV (%)", 100),
      percentageCol("irr_local", "IRR Local (%)"),
      percentageCol("irr_usd", "IRR USD (%)"),
      percentageCol("irr_local_net", "IRR Local (Excl. TFC fees)", 180),
      percentageCol("irr_usd_net", "IRR USD (Excl. TFC fees)", 170),
      {
        field: "wal",
        headerName: "WAL",
        width: 100,
        editable: editMode,
        valueParser: (params) => {
          const value = params.newValue;
          if (value === null || value === undefined || value === "")
            return null;
          const num = parseFloat(value);
          return isNaN(num) ? null : num;
        },
      },
      {
        field: "box_folder_id",
        headerName: "Box Folder ID",
        width: 140,
        editable: editMode,
        valueParser: (params) => {
          const value = params.newValue;
          if (value === null || value === undefined || value === "")
            return null;
          const num = parseInt(String(value), 10);
          return isNaN(num) ? null : num;
        },
      },
      {
        field: "target_signing",
        headerName: "Target Signing",
        editable: editMode,
        width: 140,
        cellEditor: "agDateStringCellEditor",
        valueGetter: (params) =>
          normaliseToDateInput(params.data?.target_signing),
        valueSetter: (params) => {
          if (!params.data) return false;
          const raw = params.newValue;
          if (raw == null) {
            params.data.target_signing = null;
            return true;
          }
          if (raw instanceof Date) {
            params.data.target_signing = raw.toISOString().split("T")[0];
            return true;
          }
          const trimmed = typeof raw === "string" ? raw.trim() : "";
          params.data.target_signing = trimmed || null;
          return true;
        },
        valueFormatter: dateFormatter,
      },
      {
        field: "proposed_ic_date",
        headerName: "IC Date",
        editable: editMode,
        width: 140,
        cellEditor: "agDateStringCellEditor",
        valueGetter: (params) =>
          normaliseToDateInput(params.data?.proposed_ic_date),
        valueSetter: (params) => {
          if (!params.data) return false;
          const raw = params.newValue;
          if (raw == null) {
            params.data.proposed_ic_date = null;
            return true;
          }
          if (raw instanceof Date) {
            params.data.proposed_ic_date = raw.toISOString().split("T")[0];
            return true;
          }
          const trimmed = typeof raw === "string" ? raw.trim() : "";
          params.data.proposed_ic_date = trimmed || null;
          return true;
        },
        valueFormatter: dateFormatter,
      },
      {
        field: "target_closing",
        headerName: "Target Closing",
        editable: editMode,
        width: 140,
        cellEditor: "agDateStringCellEditor",
        valueGetter: (params) =>
          normaliseToDateInput(params.data?.target_closing),
        valueSetter: (params) => {
          if (!params.data) return false;
          const raw = params.newValue;
          if (raw == null) {
            params.data.target_closing = null;
            return true;
          }
          if (raw instanceof Date) {
            params.data.target_closing = raw.toISOString().split("T")[0];
            return true;
          }
          const trimmed = typeof raw === "string" ? raw.trim() : "";
          params.data.target_closing = trimmed || null;
          return true;
        },
        valueFormatter: dateFormatter,
        comparator: (a, b) => {
          if (!a && !b) return 0;
          if (!a) return 1;
          if (!b) return -1;
          const dateA = new Date(a).getTime();
          const dateB = new Date(b).getTime();
          return dateA - dateB;
        },
      },
      {
        field: "funded_date",
        headerName: "Funded Date",
        editable: editMode,
        width: 140,
        cellEditor: "agDateStringCellEditor",
        valueGetter: (params) => normaliseToDateInput(params.data?.funded_date),
        valueSetter: (params) => {
          if (!params.data) return false;
          const raw = params.newValue;
          if (raw == null) {
            params.data.funded_date = null;
            return true;
          }
          if (raw instanceof Date) {
            params.data.funded_date = raw.toISOString().split("T")[0];
            return true;
          }
          const trimmed = typeof raw === "string" ? raw.trim() : "";
          params.data.funded_date = trimmed || null;
          return true;
        },
        valueFormatter: dateFormatter,
      },
      {
        field: "accordion_type",
        headerName: "Type",
        editable: false,
        width: 120,
        valueFormatter: (params) => {
          if (!params.value) return "";
          // Handle both string and object formats
          const typeValue =
            typeof params.value === "string"
              ? params.value
              : params.value?.id || "";
          const option = accordionTypeOptions.find(
            (opt) => opt.id === typeValue,
          );
          return option?.label || "";
        },
      },
      {
        ...(createStringSelectColumn(
          "funding_type",
          "Funding Type",
          fundingTypeOptions,
          150,
          editMode,
        ) as ColDef<AccordionRow>),
        editable: (params) => {
          // Funding type is only editable for new accordions (not yet saved) and non-baseline accordions
          if (!editMode) return false;
          if (params.data?.accordion_type === "baseline_retrospective")
            return false;

          // Only allow editing for new accordions (string ID starting with 'new-accordion-')
          const accordionId = params.data?.id;
          const isNewAccordion =
            typeof accordionId === "string" &&
            accordionId.startsWith("new-accordion-");
          return isNewAccordion;
        },
        valueGetter: (params) => {
          const fundingTypeString = params.data?.funding_type;
          if (!fundingTypeString) return null;
          if (typeof fundingTypeString === "string") {
            // Return object format for the select editor
            return { id: fundingTypeString, label: fundingTypeString };
          }
          if (
            typeof fundingTypeString === "object" &&
            fundingTypeString !== null &&
            "id" in fundingTypeString
          ) {
            return fundingTypeString;
          }
          return null;
        },
        valueSetter: (params) => {
          // Extract the string value from the object
          const stringValue =
            typeof params.newValue === "string"
              ? params.newValue
              : params.newValue?.id || null;
          if (params.data) {
            params.data.funding_type = stringValue;
            return true;
          }
          return false;
        },
      },
      {
        field: "funding_sequence_number",
        headerName: "Funding #",
        width: 100,
        editable: false,
      },
      {
        field: "funding_display",
        headerName: "Funding Display",
        width: 150,
        editable: false,
      },
    ],
    [editMode, status, currencyObjects],
  );

  return (
    <Box ref={accordionsRef} sx={{ mt: 0, scrollMarginTop: 60 }}>
      <Box sx={{ height: 400, width: "100%", mt: 0 }}>
        <div
          className="ag-theme-alpine"
          style={{ width: "100%", height: "100%" }}
        >
          <AgGridReact<AccordionRow>
            {...gridEditProps}
            ref={accordionsGridRef}
            rowData={accordions}
            columnDefs={accordionsColumnDefs}
            onCellValueChanged={onCellValueChanged}
            onGridReady={onGridReady}
            singleClickEdit={editMode}
            defaultColDef={{ resizable: true, sortable: true, filter: true }}
            rowHeight={35}
            getRowId={({ data }) => data?.id?.toString() ?? ""}
            overlayNoRowsTemplate="No addons found for this deal."
          />
        </div>
      </Box>
      <Box
        sx={{ mt: 2, display: "flex", gap: 2, justifyContent: "flex-start" }}
      >
        {editMode && (
          <>
            <CustomButton
              text="Add Addon"
              handleButtonPress={handleAddRow}
              isButtonDisabled={isSaving}
              solid={false}
            />
            <CustomButton
              text="Cancel"
              handleButtonPress={handleCancelButtonPress}
              isButtonDisabled={isSaving}
              solid={false}
              widthOverride="100px"
            />
            <CustomButton
              text="Save"
              handleButtonPress={requestSave}
              isButtonDisabled={(!dirtyGrid && !isCellEditing) || isSaving}
              isLoading={isSaving}
              isLoadingText="Saving"
              solid
              widthOverride="100px"
            />
          </>
        )}
        {!editMode && (
          <CustomButton
            text="Edit"
            handleButtonPress={() => {
              if (!canEdit) return;
              setEditMode(true);
            }}
            isButtonDisabled={!canEdit}
            solid={false}
            widthOverride="100px"
          />
        )}
      </Box>
      <ErrorDialog
        open={errorDialog.open}
        onClose={hideError}
        apiResponse={errorDialog.apiResponse}
        customMessage={errorDialog.customMessage}
        title={errorDialog.title}
      />
    </Box>
  );
};

export default DealAccordionsTable;
